import type { DrawerMessage } from '../components/AskOneDrawer'
import { clarifyOf, lookedAtOf, sourcesOf, type OneAnswer } from './wiki'

/**
 * THE ASK ONE CONVERSATION, KEPT FOR THIS TAB (1 Nov, HLP-13's session half).
 *
 * David on dev, 1 Nov: he asked One, tapped the link in the answer, and the
 * conversation was gone on the next page. Every route renders its own
 * AppShell, so navigating remounted the drawer, and the conversation was
 * useState inside it. Now it lives in ONE provider above <Routes>
 * (components/AskOneSession.tsx) and is mirrored here, to sessionStorage, so a
 * reload keeps it too.
 *
 * ── sessionStorage, NEVER localStorage ─────────────────────────────────────
 *
 * A shared site tablet keeps localStorage after the tab is closed; the next
 * person would open the drawer on somebody else's questions. sessionStorage
 * dies with the tab. It holds the person's own words, in their own tab - the
 * same thing that was already on their screen.
 *
 * ── WHOSE IT IS ─────────────────────────────────────────────────────────────
 *
 * Stamped with the signed-in AUTH USER id, which is the real person even under
 * View as (me.personId is the viewed person then). A restore for anybody else
 * returns nothing. It is cleared by Start over, by sign-out (App.tsx), and by
 * starting or ending View as (lib/viewAs.ts) - both of which reload.
 *
 * ── WHAT IT IS NOT ──────────────────────────────────────────────────────────
 *
 * Not HLP-13's server store (90 days, self-read-only, decided 17 Sep, waiting
 * for ACC-15's sixth line). Nothing here leaves the browser. Capped to the
 * turns ask-one would send anyway (`ask_one_policy.history_turns`, read by the
 * provider) and to a byte size, oldest exchanges dropped first. Versioned: a
 * stored shape this code does not know is dropped, never guessed at. Every
 * read and write is in try/catch - a private window or blocked storage must
 * leave a working drawer that simply forgets.
 *
 * Pure apart from `readAskSession` / `writeAskSession` / `clearAskSession`, so
 * the suite asserts the round trip, the caps, the owner and the version.
 */

export const ASK_SESSION_KEY = 'oneconetec.askOne'
/** Bump when the stored shape changes; an older one is dropped. */
export const ASK_SESSION_VERSION = 1
/** The most the tab keeps, in characters of JSON. */
export const ASK_SESSION_MAX_BYTES = 60_000
/** Until ask_one_limits() answers - its default (086). */
export const ASK_SESSION_DEFAULT_TURNS = 10

export type AskSession = {
  messages: DrawerMessage[]
  /** What was typed and not yet asked. */
  draft: string
  /** 126. The conversation's log row, for a prefilled link to Help. */
  logRef: string | null
  /** The page key whose chip was removed; another page brings the chip back. */
  chipOffFor: string | null
}

export const EMPTY_ASK_SESSION: AskSession = { messages: [], draft: '', logRef: null, chipOffFor: null }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The last `turns` exchanges (an exchange starts at a 'you' message). */
export function lastTurns(messages: readonly DrawerMessage[], turns: number): DrawerMessage[] {
  if (turns <= 0) return []
  let seen = 0
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].who === 'you' && ++seen === turns) return messages.slice(i)
  }
  return [...messages]
}

/** Drop the oldest exchange. */
function dropFirstTurn(messages: DrawerMessage[]): DrawerMessage[] {
  const next = messages.findIndex((m, i) => i > 0 && m.who === 'you')
  return next < 0 ? [] : messages.slice(next)
}

/** The stored text, or null when there is nothing worth keeping. */
export function serialiseAskSession(
  owner: string,
  s: AskSession,
  turns: number = ASK_SESSION_DEFAULT_TURNS,
  maxBytes: number = ASK_SESSION_MAX_BYTES,
): string | null {
  if (!owner) return null
  if (s.messages.length === 0 && !s.draft) return null
  let messages = lastTurns(s.messages, turns)
  const doc = () =>
    JSON.stringify({
      v: ASK_SESSION_VERSION,
      owner,
      messages,
      draft: s.draft.slice(0, 500),
      logRef: s.logRef && UUID.test(s.logRef) ? s.logRef : null,
      chipOffFor: s.chipOffFor,
    })
  let out = doc()
  while (out.length > maxBytes && messages.length > 0) {
    messages = dropFirstTurn(messages)
    out = doc()
  }
  return out.length > maxBytes ? null : out
}

const str = (v: unknown, cap: number): string => (typeof v === 'string' ? v.slice(0, cap) : '')
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 20) : [])

/** A stored answer, through the same checks as a fresh one (lib/wiki.ts):
 *  sources and looked-at screens re-validated, so a link drawn from storage
 *  is one the live checks would have drawn. */
export function answerFromStore(raw: unknown): OneAnswer | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.answer !== 'string' || typeof r.question !== 'string') return null
  const found = r.found === true
  const corpus = r.fromCorpus
  const request = r.request
  return {
    found,
    basis: !found ? 'none' : r.basis === 'inferred' ? 'inferred' : 'direct',
    question: str(r.question, 2000),
    answer: str(r.answer, 8000),
    used: strs(r.used),
    usedHelp: strs(r.usedHelp),
    fromCorpus: corpus === 'wiki' || corpus === 'platform' || corpus === 'both' ? corpus : 'none',
    unofficialSources: r.unofficialSources === true,
    unavailable: r.unavailable === true,
    rateLimited: r.rateLimited === true,
    logRef: typeof r.logRef === 'string' && UUID.test(r.logRef) ? r.logRef : null,
    sources: sourcesOf(r.sources),
    lookedAt: found ? lookedAtOf(r.lookedAt) : [],
    clarify: clarifyOf(r.clarify),
    readingAs: typeof r.readingAs === 'string' && r.readingAs.trim() ? r.readingAs.trim().slice(0, 160) : null,
    request: request === 'idea' || request === 'problem' || request === 'technical' ? request : null,
  }
}

/** The stored text back, for this owner and this version only; else null. */
export function restoreAskSession(raw: string | null, owner: string): AskSession | null {
  if (!raw || !owner) return null
  let d: Record<string, unknown>
  try {
    d = JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
  if (!d || d.v !== ASK_SESSION_VERSION || d.owner !== owner) return null
  const messages: DrawerMessage[] = []
  for (const m of Array.isArray(d.messages) ? (d.messages as Array<Record<string, unknown>>) : []) {
    if (m?.who === 'you' && typeof m.text === 'string' && m.text.trim()) messages.push({ who: 'you', text: m.text.slice(0, 2000) })
    else if (m?.who === 'one') {
      const answer = answerFromStore(m.answer)
      if (answer) messages.push({ who: 'one', answer })
    }
  }
  return {
    messages,
    draft: str(d.draft, 500),
    logRef: typeof d.logRef === 'string' && UUID.test(d.logRef) ? d.logRef : null,
    chipOffFor: typeof d.chipOffFor === 'string' ? d.chipOffFor.slice(0, 80) : null,
  }
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readAskSession(owner: string): AskSession | null {
  try {
    return restoreAskSession(storage()?.getItem(ASK_SESSION_KEY) ?? null, owner)
  } catch {
    return null
  }
}

export function writeAskSession(owner: string, s: AskSession, turns?: number): void {
  try {
    const st = storage()
    if (!st) return
    const out = serialiseAskSession(owner, s, turns)
    if (out) st.setItem(ASK_SESSION_KEY, out)
    else st.removeItem(ASK_SESSION_KEY)
  } catch {
    // Full or blocked: the drawer keeps working and forgets on reload.
  }
}

/** Start over, sign-out, View as. */
export function clearAskSession(): void {
  try {
    storage()?.removeItem(ASK_SESSION_KEY)
  } catch {
    // Nothing stored that can be reached, so nothing to clear.
  }
}
